import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useSetupProgress from '@forwardflow/hooks/customHooks/onboarding/useSetupProgress'
import BusinessService from '@forwardflow/services/BusinessService'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { BusinessDetails } from '@forwardflow/types'
import { IBusinessDetails } from '@forwardflow/types/onboarding'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import { Screen } from '../frame/SetupFrame'
import { useStepFooter } from '../../hooks/useStepFooter'
import { ErrorLine, Input, Select } from '../ui/primitives'
import { errorText, prettyPhone } from '../../utils/format'
import { ownerOf } from '../../utils/seats'
import { FIRST_CAMPAIGN_PATH } from '../../utils/constants'
import {
   BUSINESS_FIELDS,
   BUSINESS_SECTIONS,
   TBusinessField,
   validateBusinessField,
} from './business-fields'

/** The form holds strings only; arrays and flags are converted on submit. */
type TForm = Partial<Record<TBusinessField, string>>

const str = (v: unknown): string =>
   typeof v === 'string'
      ? v
      : Array.isArray(v)
        ? v.filter(Boolean).join(', ')
        : ''

/**
 * Step 12: business details, so texting can be approved.
 *
 * Carriers need to know who is texting (A2P registration). Everything we
 * already know is filled in: saved details, the signed-in user, the owner
 * from Follow Up Boss. Only the blanks are left. Submitting files the
 * registration and finishes setup; calls and voicemails start right away,
 * texts join once the carriers approve. "Do this later" finishes setup too.
 */
export const BusinessStep = () => {
   const navigate = useNavigate()
   const { onboardingData, setBusinessDetails, setIsOnBoardingCompleted } =
      useOnboardingStoreV2()
   const { data: progress } = useSetupProgress()
   const owner = ownerOf(onboardingData.invitedUsers)

   const prefill = useMemo<TForm>(() => {
      const saved = (progress?.businessDetails || {}) as Record<string, unknown>
      const record = (onboardingData.businessDetails || {}) as Record<
         string,
         unknown
      >
      // Later layers win only where they say something: a saved record full
      // of empty strings must not blank what the account knows.
      const merged: TForm = {}
      const layers = [record, saved]
      layers.forEach((layer) => {
         const keys = Object.keys(layer) as TBusinessField[]
         keys.forEach((k) => {
            const v = str(layer[k])
            if (v) merged[k] = v
         })
      })
      const first = (...vals: Array<string | undefined | null>) =>
         vals.find((v) => v && String(v).trim()) || ''
      // A saved choice may be stored as the option's label (the API's own
      // default is "Limited Liability Corporation"); the select needs its value.
      BUSINESS_FIELDS.filter((f) => f.kind === 'select').forEach((f) => {
         const v = merged[f.name]
         if (!v) return
         const hit = (f.options || []).find(
            (o) => o.value === v || o.label.toLowerCase() === v.toLowerCase()
         )
         merged[f.name] = hit ? hit.value : ''
      })
      return {
         ...merged,
         friendlyBusinessName: first(
            merged.friendlyBusinessName,
            merged.legalBusinessName
         ),
         email: first(merged.email, onboardingData.email, owner?.email),
         phoneNumber: first(
            merged.phoneNumber,
            owner?.phone ? prettyPhone(owner.phone) : ''
         ),
         authorizedRepresentativeFirstName: first(
            merged.authorizedRepresentativeFirstName,
            owner?.firstName
         ),
         authorizedRepresentativeLastName: first(
            merged.authorizedRepresentativeLastName,
            owner?.lastName
         ),
         authorizedRepresentativeJobPosition: first(
            merged.authorizedRepresentativeJobPosition,
            owner?.isOwner ? 'CEO' : ''
         ),
         region: first(merged.region, merged.state),
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [progress?.businessDetails, owner?.id])

   const [values, setValues] = useState<TForm>(prefill)
   const [touched, setTouched] = useState<Set<TBusinessField>>(new Set())
   const [submitting, setSubmitting] = useState(false)
   const [error, setError] = useState<string | null>(null)
   useEffect(() => {
      // Saved details arriving after mount fill the blanks, never overwrite typing.
      setValues((v) => {
         const next: TForm = { ...v }
         const keys = Object.keys(prefill) as TBusinessField[]
         keys.forEach((k) => {
            if (!next[k] && prefill[k]) next[k] = prefill[k]
         })
         return next
      })
   }, [prefill])

   const errors = useMemo(() => {
      const out: Partial<Record<TBusinessField, string>> = {}
      BUSINESS_FIELDS.forEach((f) => {
         const e = validateBusinessField(f.name, values[f.name], f.optional)
         if (e) out[f.name] = e
      })
      return out
   }, [values])
   const missing = BUSINESS_FIELDS.filter(
      (f) => !f.optional && !values[f.name]?.trim()
   ).length
   const prefilledCount = BUSINESS_FIELDS.filter((f) =>
      prefill[f.name]?.trim()
   ).length
   const isValid = Object.keys(errors).length === 0

   const set = (name: TBusinessField, value: string) =>
      setValues((v) => ({ ...v, [name]: value }))
   const touch = (name: TBusinessField) =>
      setTouched((t) => new Set(t).add(name))

   const finish = () => {
      if (!isOperatorEmail(onboardingData.email || ''))
         setIsOnBoardingCompleted(true)
      navigate(FIRST_CAMPAIGN_PATH)
   }

   const submit = async () => {
      setTouched(new Set(BUSINESS_FIELDS.map((f) => f.name)))
      if (!isValid) return
      setSubmitting(true)
      setError(null)
      try {
         const details: IBusinessDetails = {
            ...values,
            socialMediaProfileUrls: values.socialMediaProfileUrls
               ? values.socialMediaProfileUrls
                    .split(',')
                    .map((u) => u.trim())
                    .filter(Boolean)
               : undefined,
            hasTeam: undefined,
            logoObjectKey: undefined,
            logoUrl: undefined,
            businessEIIN: values.businessRegistrationNumber,
            state: values.region,
            authorizedRepresentativeEmail:
               values.authorizedRepresentativeEmail || values.email,
            authorizedRepresentativePhoneNumber: values.phoneNumber,
            businessOwnerName:
               `${values.authorizedRepresentativeFirstName || ''} ${values.authorizedRepresentativeLastName || ''}`.trim(),
            businessOwnerTitle: values.authorizedRepresentativeJobPosition,
         }
         setBusinessDetails(details)
         await BusinessService.updateBusinessDetails(
            details as unknown as BusinessDetails
         )
         finish()
      } catch (e) {
         setError(
            errorText(
               e,
               "We couldn't send your details. Check them and try again."
            )
         )
      } finally {
         setSubmitting(false)
      }
   }

   useStepFooter(
      missing
         ? `${missing} ${missing === 1 ? 'field' : 'fields'} left to send for approval`
         : 'Send for approval and finish',
      () => void submit(),
      {
         disabled: missing > 0 || submitting,
         loading: submitting,
         hint: `${prefilledCount} of ${BUSINESS_FIELDS.length} filled from your account · approval takes 3 to 5 days, calls start today.`,
         secondary: {
            label: 'Do this later, start calling now',
            onClick: finish,
         },
         testId: 'send-business',
      }
   )

   return (
      <Screen
         stepKey="business-details"
         kind="business"
         title="Confirm your business details"
         lede="Phone carriers only allow texting from a business they can identify. We filled in what we know. Complete the blanks and we file it for you."
         width="md"
      >
         <div className="space-y-7" data-testid="a2p-form">
            {BUSINESS_SECTIONS.map((sec, i) => (
               <section key={sec.id}>
                  <div className="flex items-baseline gap-3 mb-3">
                     <span className="flex shrink-0 items-center justify-center w-6 h-6 rounded-full bg-[var(--ob-primary)] text-white text-[12px] font-semibold">
                        {i + 1}
                     </span>
                     <h2 className="text-[16px] font-semibold">{sec.title}</h2>
                     <span className="text-[13px] text-[var(--ob-text-3)]">
                        {sec.blurb}
                     </span>
                  </div>
                  <div
                     className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3"
                     data-testid={`${sec.id}-form`}
                  >
                     {sec.fields.map((f) => {
                        const value = values[f.name] || ''
                        const fieldError = touched.has(f.name)
                           ? errors[f.name]
                           : undefined
                        const label = (
                           <span className="flex items-center gap-2">
                              {f.label}
                              {f.optional && (
                                 <span className="text-[10px] text-[var(--ob-text-3)] font-normal">
                                    optional
                                 </span>
                              )}
                           </span>
                        )
                        if (f.kind === 'select')
                           return (
                              <Select
                                 key={f.name}
                                 label={label}
                                 value={value}
                                 onChange={(v) => {
                                    set(f.name, v)
                                    touch(f.name)
                                 }}
                                 options={f.options || []}
                                 placeholder="Choose one"
                                 testId={`field-${f.name}`}
                                 error={fieldError}
                              />
                           )
                        return (
                           <Input
                              key={f.name}
                              label={label}
                              value={value}
                              onChange={(v) => set(f.name, v)}
                              onBlur={() => touch(f.name)}
                              placeholder={f.placeholder}
                              type={f.kind === 'email' ? 'email' : 'text'}
                              inputMode={f.kind === 'tel' ? 'tel' : undefined}
                              testId={`field-${f.name}`}
                              error={fieldError}
                              hint={f.hint}
                           />
                        )
                     })}
                  </div>
               </section>
            ))}
         </div>
         {error && <ErrorLine testId="business-error">{error}</ErrorLine>}
      </Screen>
   )
}
